/**
 * IndexedDBAdapter - Storage para datos JSON/objetos
 *
 * Usa IndexedDB via librería 'idb'.
 * Ideal para: config, metadata, theme config
 */

import { openDB } from 'idb';
import { BaseAdapter } from './BaseAdapter';

const DB_NAME = 'xhelos-storage';
const DB_VERSION = 1;

export class IndexedDBAdapter extends BaseAdapter {
  constructor(storeName) {
    super('IndexedDBAdapter');
    this.storeName = storeName;
    this.db = null;
  }

  /**
   * Inicializa la conexión a IndexedDB
   */
  async init() {
    if (this.initialized) return;

    try {
      this.db = await openDB(DB_NAME, DB_VERSION, {
        upgrade(db) {
          // Crear stores para cada categoría si no existen
          const stores = ['config', 'metadata', 'theme', 'assets', 'translations'];
          for (const store of stores) {
            if (!db.objectStoreNames.contains(store)) {
              db.createObjectStore(store);
            }
          }
        },
      });

      this.initialized = true;
    } catch (error) {
      console.error(`[IndexedDBAdapter] Failed to init:`, error);
      throw error;
    }
  }

  /**
   * Verifica si existe un key
   */
  async has(key) {
    this.ensureInitialized();
    try {
      const value = await this.db.get(this.storeName, key);
      return value !== undefined;
    } catch (error) {
      console.warn(`[IndexedDBAdapter] has() error:`, error);
      return false;
    }
  }

  /**
   * Obtiene un valor
   */
  async get(key) {
    this.ensureInitialized();
    try {
      const entry = await this.db.get(this.storeName, key);
      if (!entry) return null;

      // Si tiene wrapper con metadata, extraer data
      if (entry && typeof entry === 'object' && 'data' in entry) {
        return entry.data;
      }
      return entry;
    } catch (error) {
      console.warn(`[IndexedDBAdapter] get() error:`, error);
      return null;
    }
  }

  /**
   * Obtiene un valor con su metadata
   */
  async getWithMeta(key) {
    this.ensureInitialized();
    try {
      return await this.db.get(this.storeName, key);
    } catch (error) {
      console.warn(`[IndexedDBAdapter] getWithMeta() error:`, error);
      return null;
    }
  }

  /**
   * Guarda un valor
   * @param {string} key
   * @param {Object} value
   * @param {Object} options - { version, metadata }
   */
  async set(key, value, options = {}) {
    this.ensureInitialized();
    try {
      const entry = {
        data: value,
        savedAt: Date.now(),
        ...options,
      };
      await this.db.put(this.storeName, entry, key);
    } catch (error) {
      console.warn(`[IndexedDBAdapter] set() error:`, error);
      throw error;
    }
  }

  /**
   * Elimina un key
   */
  async delete(key) {
    this.ensureInitialized();
    try {
      await this.db.delete(this.storeName, key);
    } catch (error) {
      console.warn(`[IndexedDBAdapter] delete() error:`, error);
    }
  }

  /**
   * Limpia todo el store
   */
  async clear() {
    this.ensureInitialized();
    try {
      await this.db.clear(this.storeName);
    } catch (error) {
      console.warn(`[IndexedDBAdapter] clear() error:`, error);
    }
  }

  /**
   * Obtiene todas las keys
   */
  async keys() {
    this.ensureInitialized();
    try {
      return await this.db.getAllKeys(this.storeName);
    } catch (error) {
      console.warn(`[IndexedDBAdapter] keys() error:`, error);
      return [];
    }
  }

  /**
   * Valida si un asset tiene la versión correcta
   * @param {string} key
   * @param {string} currentVersion
   * @returns {Promise<{valid: boolean, data?: any}>}
   */
  async validateVersion(key, currentVersion) {
    const entry = await this.getWithMeta(key);

    if (!entry) {
      return { valid: false };
    }

    if (entry.version !== currentVersion) {
      return { valid: false, outdated: true };
    }

    return { valid: true, data: entry.data };
  }

  /**
   * Elimina entries con versión diferente
   * @param {string} currentVersion
   * @returns {Promise<number>} Número de entries eliminadas
   */
  async cleanOutdatedEntries(currentVersion) {
    this.ensureInitialized();
    try {
      const allKeys = await this.keys();
      let deleted = 0;

      const tx = this.db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);

      for (const key of allKeys) {
        const entry = await store.get(key);
        if (entry && entry.version && entry.version !== currentVersion) {
          await store.delete(key);
          deleted++;
        }
      }

      await tx.done;
      return deleted;
    } catch (error) {
      console.warn(`[IndexedDBAdapter] cleanOutdatedEntries() error:`, error);
      return 0;
    }
  }
}

export default IndexedDBAdapter;
